import * as React from 'react';
import {
  Bullseye,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  ExpandableSection,
  PageSection
} from '@patternfly/react-core';
import { ExclamationCircleIcon } from '@patternfly/react-icons';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Changing this resets the boundary — pass the route key so navigation clears a crash. */
  resetKey?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Last line of defence: a render-time exception anywhere in the page tree lands here
 * instead of blanking the whole application. The masthead, sidebar and navigation stay
 * mounted because this wraps only the routed content.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary] Unhandled render error:', error, info.componentStack);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // Navigating away from a broken page should give the user a working app again
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <PageSection hasBodyWrapper={false}>
        <Bullseye>
          <EmptyState
            status="danger"
            headingLevel="h2"
            icon={ExclamationCircleIcon}
            titleText="Something went wrong on this page"
          >
            <EmptyStateBody>
              The page could not be displayed. Navigating elsewhere and coming back usually
              clears it; if it keeps happening, please report it with the details below.
              <ExpandableSection toggleText="Technical details">
                <code style={{ whiteSpace: 'pre-wrap' }}>{error.message}</code>
              </ExpandableSection>
            </EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" onClick={() => this.setState({ error: null })}>
                  Try again
                </Button>
                <Button variant="link" onClick={() => window.location.reload()}>
                  Reload the application
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        </Bullseye>
      </PageSection>
    );
  }
}

export { ErrorBoundary };
